import {
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { Paging } from "types/common.types";

import { SelectOptions } from "components/Common/Select";

import { RootState } from "store/store";

import { MeetingSection } from "pages/Meetings/Form";

import { MeetingSubsection } from "./../../pages/Meetings/Form/index";
import {
  deleteMeeting,
  getAllMeetings,
  getClosestMeetings,
  getMeeting,
  getMeetingAccess,
  getMeetingFrequencies,
  getMeetingRepeatDays,
  getMeetingTypes,
  getQuorumTypes,
  getSubsectionTypes,
  getUserAllMeetings,
} from "./actions";
import {
  LiveRoomAccessDetails,
  Section,
  SingleMeeting,
  Subsection,
} from "./types";

export type SubsectionProps = {
  id: number;
  name: string;
  sectionId: number;
  content: any;
  subsection: MeetingSubsection;
};

export type SectionInfo = {
  section: MeetingSection | Section;
  parentSectionIndex?: number;
  subsection?: Subsection;
};

export type MeetingFormVisibilityType =
  | "meeting"
  | "votingBeforeMeeting"
  | "taskBeforeMeeting"
  | "votingSubsection"
  | "taskSubsection"
  | "textSubsection"
  | "milestoneSubsection"
  | "votingResult"
  | "taskResult";

export type EditedNote = {
  id: number;
  value: string;
  valueHTML: string;
  sectionId?: number;
  subsectionId?: number;
};

type MeetingFormVisibility = {
  subsectionInfo?: SubsectionProps;
  type: MeetingFormVisibilityType;
  meetingId?: number;
  sectionInfo?: SectionInfo;
  parentSectionId?: number;
  onDurationChange?: (duration: string) => void;
  votingId?: number;
  taskId?: number;
};

type InitialCamSettings = {
  hasAudio: boolean;
  hasVideo: boolean;
};

type TranscriptRecording = {
  isTranscription?: boolean;
  recordLoading?: boolean;
  isRecording?: boolean;
  recordingType?: string;
  meetingId: number;
};

export type MeetingsState = {
  error: any;
  loading: boolean;
  userMeetings: {
    meetings: SingleMeeting[];
    closestMeetings: SingleMeeting[] | null;
  };
  quorumTypes: SelectOptions[];
  meetingFrequencies: SelectOptions[];
  meetingRepeatDays: SelectOptions[];
  meetingTypes: SelectOptions[];
  subsectionTypes: SelectOptions[];
  meetingFormVisibility: MeetingFormVisibility;
  liveRoomAccessDetails: LiveRoomAccessDetails | null;
  initialCamSettings: InitialCamSettings | null;
  editedNotes: { [key: number]: EditedNote } | null;
  touchedNotes: { [key: number]: EditedNote } | null;
  isSummaryEditMode: boolean;
  initialWorkspaceId: number | null;
  meta: Paging;
  initialMeetingUserId: number | null;
  transcriptRecording: TranscriptRecording[];
};

const initialState: MeetingsState = {
  error: false,
  loading: false,
  userMeetings: {
    meetings: [],
    closestMeetings: null,
  },
  quorumTypes: [],
  meetingFrequencies: [],
  meetingRepeatDays: [],
  meetingTypes: [],
  subsectionTypes: [],
  meetingFormVisibility: {
    type: "meeting",
  },
  liveRoomAccessDetails: null,
  initialCamSettings: null,
  editedNotes: null,
  touchedNotes: null,
  isSummaryEditMode: false,
  initialWorkspaceId: null,
  meta: {} as Paging,
  initialMeetingUserId: null,
  transcriptRecording: [],
};

export const meetingAdapter = createEntityAdapter<SingleMeeting>();

export const meetingsSlice = createSlice({
  name: "meetings",
  initialState: meetingAdapter.getInitialState<MeetingsState>(initialState),
  reducers: {
    updateTranscriptRecording: (
      state,
      action: PayloadAction<TranscriptRecording>
    ) => {
      const { meetingId, ...updatedFields } = action.payload;

      if (!state.transcriptRecording) {
        state.transcriptRecording = [];
      }

      const index = state.transcriptRecording.findIndex(
        (item) => item.meetingId === meetingId
      );

      if (index !== -1) {
        state.transcriptRecording[index] = {
          ...state.transcriptRecording[index],
          ...updatedFields,
        };
      } else {
        state.transcriptRecording.push({ meetingId, ...updatedFields });
      }
    },
    removeTranscriptRecording: (state, action: PayloadAction<number>) => {
      const meetingIdToRemove = action.payload;
      state.transcriptRecording = state.transcriptRecording.filter(
        (item) => item.meetingId !== meetingIdToRemove
      );
    },
    meetingSetAll: meetingAdapter.setAll,
    meetingAdd: meetingAdapter.addOne,
    meetingRemove: meetingAdapter.removeOne,
    meetingUpdate: meetingAdapter.updateOne,
    meetingAddOne: meetingAdapter.addOne,
    setMeetingFormVisibility: (
      state,
      action: PayloadAction<MeetingFormVisibility>
    ) => {
      state.meetingFormVisibility = action.payload;
    },
    addNote: (state, action: PayloadAction<EditedNote>) => {
      state.editedNotes = {
        ...state.editedNotes,
        [Number(action.payload?.id)]: action.payload,
      };
    },
    touchNote: (state, action: PayloadAction<EditedNote>) => {
      state.touchedNotes = {
        ...state.touchedNotes,
        [Number(action.payload?.id)]: action.payload,
      };
    },
    removeNote: (state, action: PayloadAction<number>) => {
      delete state.editedNotes?.[action.payload];
    },
    resetNotes: (state) => {
      state.editedNotes = null;
    },
    resetTouchedNotes: (state) => {
      state.touchedNotes = null;
    },
    setIsSummaryEditMode: (state, action: PayloadAction<boolean>) => {
      state.isSummaryEditMode = action.payload;
    },
    setInitialWorkspaceId: (state, action: PayloadAction<number | null>) => {
      state.initialWorkspaceId = action.payload;
    },
    updateMeeting: (state, action: PayloadAction<SingleMeeting>) => {
      meetingAdapter.setOne(state, action.payload);
    },
    setInitialMeetingUserId: (state, action: PayloadAction<number | null>) => {
      state.initialMeetingUserId = action.payload;
    },
    setInitialCamSetings: (
      state,
      action: PayloadAction<InitialCamSettings | null>
    ) => {
      state.initialCamSettings = action.payload;
    },
  },
  extraReducers: (builder) => {
    //delete meeting
    builder.addCase(deleteMeeting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(deleteMeeting.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deleteMeeting.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      meetingAdapter.removeOne(state, action.meta.arg);
    });

    //user meetings
    builder.addCase(getUserAllMeetings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getUserAllMeetings.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getUserAllMeetings.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.meta = action.payload.meta;
      meetingAdapter.setAll(state, action.payload.data);
    });

    //all meetings
    builder.addCase(getAllMeetings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getAllMeetings.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getAllMeetings.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.meta = action.payload.meta;
      meetingAdapter.setAll(state, action.payload.data);
    });

    //get meeting
    builder.addCase(getMeeting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getMeeting.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getMeeting.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      meetingAdapter.setOne(state, action.payload.data);
    });

    //quorum types
    builder.addCase(getQuorumTypes.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(getQuorumTypes.pending, (state) => {
      state.error = false;
    });
    builder.addCase(getQuorumTypes.fulfilled, (state, action) => {
      state.error = false;
      state.quorumTypes = action.payload.data;
    });

    //meeting frequencies
    builder.addCase(getMeetingFrequencies.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(getMeetingFrequencies.pending, (state) => {
      state.error = false;
    });
    builder.addCase(getMeetingFrequencies.fulfilled, (state, action) => {
      state.error = false;
      state.meetingFrequencies = action.payload.data;
    });

    //meeting repeat days
    builder.addCase(getMeetingRepeatDays.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(getMeetingRepeatDays.pending, (state) => {
      state.error = false;
    });
    builder.addCase(getMeetingRepeatDays.fulfilled, (state, action) => {
      state.error = false;
      state.meetingRepeatDays = action.payload.data;
    });

    //meeting types
    builder.addCase(getMeetingTypes.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(getMeetingTypes.pending, (state) => {
      state.error = false;
    });
    builder.addCase(getMeetingTypes.fulfilled, (state, action) => {
      state.error = false;
      state.meetingTypes = action.payload.data;
    });

    //meeting access details
    builder.addCase(getMeetingAccess.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getMeetingAccess.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getMeetingAccess.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.liveRoomAccessDetails = action.payload;
    });

    //meeting subsection types
    builder.addCase(getSubsectionTypes.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(getSubsectionTypes.pending, (state) => {
      state.error = false;
    });
    builder.addCase(getSubsectionTypes.fulfilled, (state, action) => {
      state.error = false;
      state.subsectionTypes = action.payload.data;
    });

    //closes meeting
    builder.addCase(getClosestMeetings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getClosestMeetings.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getClosestMeetings.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.userMeetings.closestMeetings = action.payload.data;
    });
  },
});

//selectors

export const { selectAll: selectAllMeetings, selectById: selectMeetingById } =
  meetingAdapter.getSelectors<RootState>((state) => state.meetings);
export const meetingsLoading = (state: RootState) => state.meetings.loading;

export const {
  setMeetingFormVisibility,
  addNote,
  removeNote,
  resetNotes,
  setIsSummaryEditMode,
  setInitialWorkspaceId,
  updateMeeting,
  setInitialMeetingUserId,
  setInitialCamSetings,
  resetTouchedNotes,
  touchNote,
  updateTranscriptRecording,
  removeTranscriptRecording,
} = meetingsSlice.actions;
// Action creators are generated for each case reducer function
// export const {} = workspacesSlice.actions;
export default meetingsSlice.reducer;
